import { Card, CardContent } from "@/components/ui/card";
import { getDashboard } from "@/api/mockData";
import { t, formatRub, formatGNK, type Lang } from "@/lib/i18n";

interface DashboardProps {
  lang: Lang;
  onNavigateToPool: (id: string) => void;
  onNavigateToPools: () => void;
}

// 7-day mock reward data
const rewardBars = [5.76, 6.32, 5.87, 6.21, 5.94, 6.08, 4.92];
const barDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const barDaysEn = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export function Dashboard({ lang, onNavigateToPool, onNavigateToPools }: DashboardProps) {
  const data = getDashboard();
  const maxBar = Math.max(...rewardBars);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="pt-3 flex items-end justify-between">
        <div>
          <p className="text-muted-foreground text-xs tracking-wide">{t("dash.welcome", lang)}</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-foreground mt-1">
            Алексей
          </h1>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
          <span className="relative flex h-1.5 w-1.5">
            <span className="net-pulse absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
          </span>
          <span className="text-[9px] font-semibold text-white/80 tracking-wider uppercase">
            {lang === "ru" ? "Онлайн" : "Online"}
          </span>
        </div>
      </div>

      {/* Hero Metrics — two primary numbers */}
      <div className="glass-card-featured rounded-2xl p-6 relative overflow-hidden animate-fade-in-up">
        {/* Decorative corner accent */}
        <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none opacity-[0.04]" style={{
          background: "radial-gradient(circle at top right, rgba(255,255,255,0.3), transparent 70%)"
        }} />
        <div className="grid grid-cols-2 gap-8 relative">
          <div className="accent-left-cyan">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-medium mb-2.5">
              {t("dash.totalInvested", lang)}
            </p>
            <p className="metric-hero text-[28px] text-primary">{formatRub(data.totalInvested)}</p>
          </div>
          <div className="accent-left-amber">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-medium mb-2.5">
              {t("dash.totalMined", lang)}
            </p>
            <p className="metric-hero text-[28px] text-white">{formatGNK(data.totalMined)}<span className="text-[11px] font-semibold text-white/40 ml-1.5">GNK</span></p>
          </div>
        </div>
        <div className="divider-gradient my-5" />
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/[0.05] flex items-center justify-center border border-white/[0.08]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/60">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground/60">{t("dash.activePools", lang)}</p>
              <p className="font-mono-num text-base font-bold text-foreground">{data.activePools}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/[0.05] flex items-center justify-center border border-white/[0.08]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/60">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
              </svg>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground/60">{t("dash.currentYield", lang)}</p>
              <p className="font-mono-num text-base font-bold text-foreground">{data.currentYield}%<span className="text-[10px] font-normal text-muted-foreground/50 ml-0.5">{t("dash.perMonth", lang)}</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* 7-Day Reward Mini Chart */}
      <section className="glass-card rounded-2xl p-5 animate-fade-in-up" style={{ animationDelay: "200ms" }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="section-label">
              {lang === "ru" ? "Награды за 7 дней" : "7-Day Rewards"}
            </h3>
            <p className="font-mono-num text-xl font-bold text-white mt-1.5">
              +{formatGNK(rewardBars.reduce((s, v) => s + v, 0))} <span className="text-xs font-semibold text-white/40">GNK</span>
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08]">
            <span className="w-1.5 h-1.5 rounded-full mining-beacon" />
            <span className="text-[10px] font-semibold text-white/70 tracking-wide">
              {lang === "ru" ? "Майнинг" : "Mining"}
            </span>
          </div>
        </div>
        <div className="flex items-end gap-2 h-20">
          {rewardBars.map((val, i) => {
            const pct = (val / maxBar) * 100;
            const isToday = i === rewardBars.length - 1;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full relative" style={{ height: "56px" }}>
                  <div
                    className={`absolute bottom-0 left-0.5 right-0.5 rounded-md bar-grow ${isToday ? "glow-red" : ""}`}
                    style={{
                      height: `${pct}%`,
                      background: isToday
                        ? "linear-gradient(to top, #dc2626, #ef4444)"
                        : "rgba(255, 255, 255, 0.08)",
                      animationDelay: `${i * 80}ms`,
                    }}
                  />
                </div>
                <span className={`text-[9px] font-medium ${isToday ? "text-white" : "text-muted-foreground/60"}`}>
                  {lang === "ru" ? barDays[i] : barDaysEn[i]}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* My Pools */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-label">
            {t("dash.myPools", lang)}
          </h2>
          <button
            onClick={onNavigateToPools}
            className="text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            {t("pools.all", lang)}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </div>
        <div className="space-y-3">
          {data.userPools.map((pool, i) => (
            <button
              key={pool.id}
              onClick={() => onNavigateToPool(pool.id)}
              className="w-full text-left glass-card rounded-2xl overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${(i + 4) * 80}ms` }}
            >
              {/* Top accent line */}
              <div className="h-[2px]" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.03), transparent)' }} />
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-[10px] font-black font-display"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      GPU
                    </div>
                    {pool.status === "mining" && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-white/50 border-2 border-background" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-bold truncate">{pool.name}</span>
                      <StatusBadge status={pool.status} lang={lang} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-muted-foreground">{pool.gpuModel}</span>
                      {pool.status === "mining" && (
                        <span className="text-[12px] font-mono-num font-bold text-white bg-white/8 px-2 py-0.5 rounded-md border border-white/10">
                          +{pool.dailyRewardGNK} GNK<span className="text-muted-foreground/60 font-normal text-[10px]">{t("dash.perMonth", lang) === "/мес" ? "/д" : "/d"}</span>
                        </span>
                      )}
                    </div>
                    {pool.status === "collecting" && (
                      <div className="mt-2.5">
                        <div className="h-1.5 bg-muted/60 rounded-full overflow-hidden">
                          <div
                            className="progress-bar h-full transition-all duration-700"
                            style={{ width: `${(pool.collectedAmount / pool.targetAmount) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1.5">
                          <span className="text-[10px] text-muted-foreground font-mono-num">
                            {formatRub(pool.collectedAmount)}
                          </span>
                          <span className="text-[10px] font-mono-num font-semibold text-foreground/80">
                            {formatRub(pool.targetAmount)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Recent Activity */}
      <section>
        <h2 className="section-label mb-4">
          {t("dash.recentActivity", lang)}
        </h2>
        <Card className="glass-card border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {data.recentActivity.map((item, i) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 px-4 py-3.5 ${
                  i !== data.recentActivity.length - 1 ? "border-b border-white/[0.03]" : ""
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-secondary/60 flex items-center justify-center text-sm shrink-0">{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate">{item.description}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{item.date}</p>
                </div>
                {item.amount && (
                  <span
                    className={`text-[13px] font-mono-num font-bold ${
                      item.currency === "GNK" ? "text-white" : "text-foreground/80"
                    }`}
                  >
                    {item.currency === "GNK" ? "+" : ""}
                    {item.currency === "RUB" ? formatRub(item.amount) : formatGNK(item.amount)}
                    {item.currency === "GNK" ? " GNK" : ""}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}


function StatusBadge({ status, lang }: { status: string; lang: Lang }) {
  const labels: Record<string, string> = {
    mining: t("pools.status.mining", lang),
    collecting: t("pools.status.collecting", lang),
    completed: t("pools.status.completed", lang),
  };

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.08] text-white/80">
      <span className={`w-1.5 h-1.5 rounded-full ${status === "mining" ? "mining-beacon" : "bg-white/40"}`} />
      {labels[status] ?? status}
    </span>
  );
}
